import { useState, useEffect, useCallback } from 'react';
import { client } from '@/integrations/neon/client';
import { useAuth } from './useAuth';

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_type: 'free' | 'annual' | 'lifetime';
  status: 'active' | 'cancelled' | 'expired' | 'past_due';
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  stripe_payment_intent_id?: string;
  expires_at?: string;
  decision_limit: number;
  created_at: string;
  updated_at: string;
}

/**
 * Review dates allowed per decision on the free plan. Premium plans are
 * uncapped.
 */
export const FREE_REVIEW_DATE_LIMIT = 3;

interface SubscriptionMethods {
  refreshSubscription: () => Promise<void>;
  /** An active paid plan (annual or lifetime). */
  isPremium: () => boolean;
  /** The activity log is premium-only. */
  canUseActivityLog: () => boolean;
  /** Review dates allowed per decision: FREE_REVIEW_DATE_LIMIT, or Infinity. */
  getReviewDateLimit: () => number;
  /** Whether another review date may be added to a decision of this size. */
  canAddReviewDate: (currentReviewDateCount: number) => boolean;
  getStatusMessage: () => string;
}

export function useSubscription(): {
  subscription: UserSubscription | null;
  loading: boolean;
  error: string | null;
} & SubscriptionMethods {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await client
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription yet (Neon Auth has no signup trigger like the old
          // Supabase auth.users trigger) - create the default free plan row.
          const { data: created, error: createError } = await client
            .from('user_subscriptions')
            .insert({ user_id: user.id, plan_type: 'free', status: 'active', decision_limit: 20 })
            .select()
            .single();

          if (createError) {
            console.error('Error creating subscription:', createError);
            setError(createError.message);
          } else {
            setSubscription(created as UserSubscription);
          }
        } else {
          console.error('Error fetching subscription:', error);
          setError(error.message);
        }
      } else {
        setSubscription(data as UserSubscription);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to fetch subscription');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const refreshSubscription = useCallback(async () => {
    setLoading(true);
    await fetchSubscription();
  }, [fetchSubscription]);

  // Feature gating
  // ==============
  // Creating decisions is unlimited on every plan. Only two things are
  // premium: the activity log, and review dates beyond FREE_REVIEW_DATE_LIMIT.
  //
  // A missing subscription row resolves to the free tier, never to "no
  // access". The row is created lazily on first load, so treating its absence
  // as a denial previously blocked a brand-new user from saving their very
  // first decision.

  const isPremium = useCallback((): boolean => {
    if (!subscription) return false;
    if (subscription.status !== 'active') return false;
    return subscription.plan_type === 'annual' || subscription.plan_type === 'lifetime';
  }, [subscription]);

  const canUseActivityLog = useCallback((): boolean => isPremium(), [isPremium]);

  const getReviewDateLimit = useCallback(
    (): number => (isPremium() ? Infinity : FREE_REVIEW_DATE_LIMIT),
    [isPremium],
  );

  const canAddReviewDate = useCallback(
    (currentReviewDateCount: number): boolean => currentReviewDateCount < getReviewDateLimit(),
    [getReviewDateLimit],
  );

  const getStatusMessage = useCallback((): string => {
    if (!subscription) return 'Free Plan';

    switch (subscription.plan_type) {
      case 'free':
        return 'Free Plan';
      case 'annual':
        return 'Annual Plan';
      case 'lifetime':
        return 'Lifetime Plan';
      default:
        return 'Unknown Plan';
    }
  }, [subscription]);

  return {
    subscription,
    loading,
    error,
    refreshSubscription,
    isPremium,
    canUseActivityLog,
    getReviewDateLimit,
    canAddReviewDate,
    getStatusMessage,
  };
}